import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch, beginOptimistic, unwrap } from '@/shared/api'
import { toast } from '@/shared/ui/toast'
import { patchTaskCache } from '../lib/cache'
import { taskErrorText } from '../lib/error-text'
import { buildPatchBody, optimisticTaskPatch, type TaskChange } from '../lib/patch'
import type { Task } from '../model/schema'
import { type TaskDto, toTask } from './mappers'

export const taskKeys = {
  all: ['tasks'] as const,
  detail: (id: string) => [...taskKeys.all, 'detail', id] as const,
  board: (boardId: string) => [...taskKeys.all, 'board', boardId] as const,
  mine: () => [...taskKeys.all, 'mine'] as const,
  employee: (id: string) => [...taskKeys.all, 'employee', id] as const,
  gantt: (boardId: string) => [...taskKeys.all, 'gantt', boardId] as const,
  dependencies: (id: string) => [...taskKeys.all, 'dependencies', id] as const,
  links: (id: string) => [...taskKeys.all, 'links', id] as const,
  search: (query: string, tag: string | null) => [...taskKeys.all, 'search', query, tag] as const,
}

export function taskQueryOptions(id: string) {
  return queryOptions({
    queryKey: taskKeys.detail(id),
    queryFn: async (): Promise<Task> => toTask(unwrap<TaskDto>(await apiGet(`/tasks/${id}`))),
  })
}

// Плоский список активных задач доски — дерево строится на клиенте по parentId.
export function boardTasksQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: taskKeys.board(boardId),
    queryFn: async (): Promise<Task[]> => {
      const list = unwrap<TaskDto[]>(await apiGet(`/boards/${boardId}/tasks`)) ?? []
      return list.map(toTask)
    },
  })
}

export const myTasksQueryOptions = queryOptions({
  queryKey: taskKeys.mine(),
  queryFn: async (): Promise<Task[]> => {
    const list = unwrap<TaskDto[]>(await apiGet('/employees/me/tasks')) ?? []
    return list.map(toTask)
  },
})

// Задачи произвольного сотрудника (страница профиля) — как myTasks, но по id.
export function employeeTasksQueryOptions(id: string) {
  return queryOptions({
    queryKey: taskKeys.employee(id),
    queryFn: async (): Promise<Task[]> => {
      const list = unwrap<TaskDto[]>(await apiGet(`/employees/${id}/tasks`)) ?? []
      return list.map(toTask)
    },
  })
}

// Правка полей задачи (PATCH /tasks/{id}, тройная clear_* семантика). Оптимистично
// вливаем change в кэш; features (edit/estimate/deadline) зовут с нужными полями.
export function useUpdateTaskMutation(task: { id: string; boardId: string }) {
  const qc = useQueryClient()
  const touched = [taskKeys.detail(task.id), taskKeys.board(task.boardId), taskKeys.mine()]
  return useMutation({
    mutationFn: (change: TaskChange) => apiPatch(`/tasks/${task.id}`, buildPatchBody(change)),
    onMutate: async (change): Promise<{ rollback?: () => void }> => {
      const rollback = await beginOptimistic(qc, touched)
      patchTaskCache(qc, task.id, optimisticTaskPatch(change))
      return { rollback }
    },
    onError: (err, _vars, ctx) => {
      ctx?.rollback?.()
      toast.error(taskErrorText(err, 'Не удалось сохранить изменения'))
    },
    onSettled: () => {
      for (const key of touched) void qc.invalidateQueries({ queryKey: key })
    },
  })
}
